import 'reflect-metadata'

import {
  IsDateOnly,
  IsDecimalNumber,
  IsEmptyWhenProperty,
  IsISO8601WithTimezone,
  IsLaterThan,
  RequireProperty,
} from '@agroone/helpers'
import { plainToInstance, Transform, Type } from 'class-transformer'
import {
  ArrayMaxSize,
  ArrayNotEmpty,
  IsArray,
  IsBoolean,
  IsEnum,
  IsIn,
  IsInt,
  IsNotEmpty,
  IsNumber,
  IsOptional,
  IsPositive,
  IsString,
  Max,
  Min,
  Validate,
  ValidateNested,
} from 'class-validator'
import { RateUnits } from '../enums/rate-units.enum'
import { SaveForeignMaterial } from './save-foreign-material'
import { SaveLocation } from './save-location'
import { SaveReporting } from './save-reporting'

export class SaveInterventionFoodSafety {
  @IsNumber()
  @IsOptional()
  id?: number

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveLocation)
  location?: SaveLocation

  @IsNumber()
  @Validate(IsDecimalNumber, [1])
  @Min(0)
  @IsOptional()
  bufferZone?: number

  @IsString()
  @IsIn(['m', 'ft', 'ac', 'ha'])
  @IsOptional()
  bufferZoneUnit?: string

  @Validate(IsDateOnly)
  @IsNotEmpty()
  completedDate!: string

  @IsNumber()
  @IsOptional()
  locationId?: number
}

export class SaveInterventionNutrients {
  @IsNumber()
  @IsNotEmpty()
  nutrientId!: number

  @IsNumber()
  @Validate(IsDecimalNumber, [2])
  @Min(0)
  @Max(100)
  @IsNotEmpty()
  value!: number
}

export class SaveInterventionIrrigation {
  @IsNumber()
  @IsOptional()
  id?: number

  @IsNotEmpty()
  @Validate(IsDateOnly)
  completedStartingDate!: string

  @IsNotEmpty()
  @Validate(IsDateOnly)
  @IsLaterThan('completedStartingDate')
  completedEndingDate!: string

  @IsNumber()
  @Min(0)
  @Validate(IsDecimalNumber, [4])
  area!: number

  @IsNumber()
  @Min(0)
  @Validate(IsDecimalNumber, [4])
  amount!: number

  @IsString()
  @IsNotEmpty()
  areaUnit!: string

  @IsString()
  @IsNotEmpty()
  amountUnit!: string

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveInterventionNutrients)
  nutrients?: SaveInterventionNutrients[]
}

export class SaveInterventionMechanicalWeeding {
  @IsNumber()
  @IsOptional()
  id?: number

  @Transform(({ value }) => (typeof value === 'string' ? Number(value.replace(',', '.')) : value))
  @IsNumber()
  @IsPositive()
  area!: number

  @IsString()
  @IsOptional()
  areaUnit?: string

  @Validate(IsDateOnly)
  @IsNotEmpty()
  completedDate!: string
}

export class SaveInterventionOtherOperation {
  @IsNumber()
  @IsOptional()
  id?: number

  @IsString()
  @IsNotEmpty()
  operationType!: string

  @IsString()
  @IsNotEmpty()
  area!: string

  @IsString()
  @IsOptional()
  areaUnit?: string

  @Validate(IsDateOnly)
  @IsNotEmpty()
  completedDate!: string
}

export class SaveInterventionPesticidesProduct {
  @IsNumber()
  @IsNotEmpty()
  pesticideId!: number

  @IsNumber()
  @IsOptional()
  targetCropId?: number

  @Validate(IsDecimalNumber, [4])
  @IsNumber()
  @Min(0)
  @IsNotEmpty()
  rate!: number

  @IsString()
  @IsEnum(RateUnits)
  @IsNotEmpty()
  rateUnit!: string

  @IsString()
  @IsNotEmpty()
  rateAreaUnit!: string
}

export class SaveInterventionPesticide {
  @IsNumber()
  @IsOptional()
  id?: number

  @IsNumber()
  @Validate(IsDecimalNumber, [2])
  @Min(0)
  @IsNotEmpty()
  waterAmount!: number

  @IsString()
  @IsIn(['l', 'ml', 'gal'])
  @IsNotEmpty()
  waterUnit!: string

  @IsString()
  @IsNotEmpty()
  waterAreaUnit!: string

  @IsArray()
  @ValidateNested()
  @Type(() => SaveInterventionPesticidesProduct)
  @ArrayNotEmpty()
  products!: SaveInterventionPesticidesProduct[]

  @IsString()
  @IsOptional()
  weatherConditions?: string

  @Validate(IsDateOnly)
  @IsNotEmpty()
  completedDate!: string

  @IsNumber()
  @Validate(IsDecimalNumber, [2])
  @Min(0)
  @IsOptional()
  @RequireProperty('windSpeedUnit')
  windSpeed?: number

  @IsString()
  @IsOptional()
  @RequireProperty('windSpeed')
  windSpeedUnit?: string

  @IsString()
  @IsOptional()
  applicationEquipment?: string

  @IsNumber()
  @Min(0)
  @Validate(IsDecimalNumber, [4])
  @IsNotEmpty()
  area!: number | undefined

  @IsString()
  @IsNotEmpty()
  areaUnit!: string | undefined

  @IsString()
  @IsOptional()
  windDirection?: string

  @IsNumber()
  @Validate(IsDecimalNumber, [2])
  @IsOptional()
  @RequireProperty('temperatureUnit')
  temperature?: number

  @IsString()
  @IsOptional()
  @RequireProperty('temperature')
  temperatureUnit?: string

  @IsString()
  @IsOptional()
  targetPest?: string

  @IsInt()
  @IsPositive()
  @IsOptional()
  @IsEmptyWhenProperty('supplierIsFarm')
  supplierId?: number

  @IsBoolean()
  @IsOptional()
  supplierIsFarm?: boolean
}

export class SaveInterventionNutrient {
  @IsNotEmpty()
  @Validate(IsDateOnly)
  targetDate!: string

  @IsNumber()
  @Validate(IsDecimalNumber, [4])
  @Min(0)
  @RequireProperty('areaUnit')
  area!: number | undefined

  @IsString()
  @RequireProperty('area')
  @IsNotEmpty()
  areaUnit!: string | undefined

  @IsNumber()
  @Validate(IsDecimalNumber, [2])
  @Min(0)
  @RequireProperty('amountUnit')
  amount!: number

  @IsString()
  @RequireProperty('amount')
  @IsNotEmpty()
  amountUnit!: string

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveInterventionNutrients)
  nutrients?: SaveInterventionNutrients[]

  @IsOptional()
  @IsString()
  nameProduct?: string
}

export class SaveIntervention extends SaveReporting {
  public static override build(raw: any) {
    return plainToInstance(SaveIntervention, raw, { enableImplicitConversion: true })
  }

  @IsNumber()
  @IsOptional()
  id?: number

  @IsNotEmpty()
  @Validate(IsDateOnly)
  date!: string

  @IsNumber()
  @IsNotEmpty()
  completedBy!: number | null | undefined

  @IsString()
  @IsNotEmpty()
  type!: string

  @IsString()
  @IsNotEmpty()
  subType!: string

  @IsNumber()
  @IsNotEmpty()
  cropId!: number

  @IsArray()
  @IsOptional()
  crops?: number[]

  @IsString()
  @IsNotEmpty()
  region!: string

  @IsNumber()
  @IsNotEmpty()
  regionId!: number

  @IsNumber()
  @IsOptional()
  recommendationId?: number

  @IsNumber()
  @IsOptional()
  workOrderId?: number

  @IsString()
  @IsOptional()
  note?: string

  @IsArray()
  @ArrayMaxSize(3)
  @IsOptional()
  picturesPath?: string[]

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveInterventionFoodSafety)
  foodSafety?: SaveInterventionFoodSafety

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveInterventionMechanicalWeeding)
  mechanicalWeeding?: SaveInterventionMechanicalWeeding

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveInterventionOtherOperation)
  otherOperation?: SaveInterventionOtherOperation

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveInterventionPesticide)
  pesticides?: SaveInterventionPesticide

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveInterventionIrrigation)
  irrigation?: SaveInterventionIrrigation

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveForeignMaterial)
  foreignMaterial?: SaveForeignMaterial

  @IsOptional()
  @ValidateNested()
  @Type(() => SaveInterventionNutrient)
  nutrient?: SaveInterventionNutrient

  @IsString()
  @IsOptional()
  @Validate(IsISO8601WithTimezone)
  disablingDate?: string

  @IsNumber()
  @IsOptional()
  externalId?: number

  @IsInt()
  @IsPositive()
  @IsOptional()
  externalClientId?: number
}
